import { Cached, Delete, Edit, MenuOpen, Newspaper } from '@mui/icons-material'
import { Box, Button, Grid, IconButton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableOptions,
} from 'material-react-table'
import { useConfirm } from 'material-ui-confirm'
import { FunctionComponent, useMemo, useState } from 'react'

import AuditIconButton from '../../../../base/components/Auditoria/AuditIconButton'
import { SimpleContainerBox } from '../../../../base/components/Container/SimpleBox'
import SimpleContainer from '../../../../base/components/Container/SimpleContainer'
import SimpleMenu, { SimpleMenuItem } from '../../../../base/components/MyMenu/SimpleMenu'
import StackMenu from '../../../../base/components/MyMenu/StackMenu'
import { StackMenuItem } from '../../../../base/components/MyMenu/StackMenuItem'
import Breadcrumb from '../../../../base/components/Template/Breadcrumb/Breadcrumb'
import { PAGE_DEFAULT, PageInputProps } from '../../../../interfaces'
import { genApiQuery } from '../../../../utils/helper'
import { MuiTableAdvancedOptionsProps } from '../../../../utils/muiTable/muiTableAdvancedOptionsProps'
import { notDanger, notSuccess } from '../../../../utils/notification'
import { swalClose, swalException, swalLoading } from '../../../../utils/swal'
import { almacenRoutesMap } from '../../almacenRoutes'
import { apiAlmacenEliminar } from '../../api/apiAlmacenEliminar'
import { apiAlmacen } from '../../api/apiAlmacenListado'
import { AlmacenBaseProp, AlmacenProp } from '../../interfaces'
import AlmacenActualizarDialog from '../actualizar/AlmacenActualizarDialog'
import AlmacenRegistroDialog from '../registro/AlmacenRegistroDialog'

interface OwnProps {}

type Props = OwnProps

const tableColumns: MRT_ColumnDef<AlmacenProp>[] = [
  {
    accessorKey: 'codigoAlmacen',
    header: 'Código',
    enableSorting: true, // Habilita el ordenamiento solo para esta columna
    enableColumnFilter: true, // Habilita el filtro solo para esta columna
    size: 100,
  },
  {
    accessorFn: (row) => `${row.sucursal.codigo} - ${row.sucursal.direccion}`,
    id: 'sucursal',
    header: 'Sucursal',
    enableSorting: false, // Deshabilita el ordenamiento para esta columna
    enableColumnFilter: true, // Deshabilita el filtro para esta columna
  },
  {
    accessorKey: 'nombre',
    header: 'Nombre',
    enableSorting: false, // Habilita el ordenamiento solo para esta columna
    enableColumnFilter: true, // Habilita el filtro solo para esta columna
  },
  {
    accessorKey: 'ubicacion',
    header: 'Ubicación',
    enableSorting: false, // Habilita el ordenamiento solo para esta columna
    enableColumnFilter: true, // Habilita el filtro solo para esta columna
  },
  {
    accessorKey: 'tipo',
    header: 'Tipo',
    enableSorting: false, // Habilita el ordenamiento solo para esta columna
    enableColumnFilter: true, // Habilita el filtro solo para esta columna
  },
  {
    accessorKey: 'prioridad',
    header: 'Prioridad',
    enableSorting: false, // Habilita el ordenamiento solo para esta columna
    enableColumnFilter: true, // Habilita el filtro solo para esta columna
  },
]

const AlmacenListado: FunctionComponent<Props> = (props) => {
  const [openOpenNuevoAlmacen, setOpenNuevoAlmacen] = useState<boolean>(false)
  const [openActualizarAlmacen, setOpenActualizarAlmacen] = useState<boolean>(false)
  const [selectedAlmacen, setSelectedAlmacen] = useState<AlmacenProp | null>(null)
  // ESTADO DATATABLE
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: PAGE_DEFAULT.page,
    pageSize: PAGE_DEFAULT.limit,
  })
  const [rowCount, setRowCount] = useState(0)
  const [isRefetching, setIsRefetching] = useState(false)
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  // FIN ESTADO DATATABLE
  const handleRefreshTable = async () => {
    setIsRefetching(true) // Establece el estado de "isRefetching" como verdadero para mostrar el indicador de carga

    try {
      await refetch() // Realiza la recarga de los datos utilizando la función "refetch" proporcionada por react-query
    } catch (error) {
      console.error('Error al recargar los datos:', error)
    }

    setIsRefetching(false) // Establece el estado de "isRefetching" como falso para ocultar el indicador de carga
  }
  const { data, isError, isLoading, status, refetch } = useQuery({
    queryKey: [
      'almacenListado',
      columnFilters,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ],
    queryFn: async () => {
      const query = genApiQuery(columnFilters)
      const fetchPagination: PageInputProps = {
        ...PAGE_DEFAULT,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        reverse: sorting.length <= 0,
        query,
      }
      const { pageInfo, docs } = await apiAlmacen(fetchPagination)
      setRowCount(pageInfo.totalDocs)
      return docs
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
  })

  const columns = useMemo(() => tableColumns, [])
  const confirm = useConfirm()
  /**
   * Eliminación de un almacen
   * @param row
   */
  const handleDeleteRow = async (row: AlmacenProp) => {
    const { confirmed } = await confirm({
      title: 'Confirmar Eliminación',
      content: `¿Estás seguro de que deseas eliminar el registro "${row.codigoAlmacen} - ${row.nombre}"?`,
    })
    if (!confirmed) return

    swalLoading()
    try {
      const resp = await apiAlmacenEliminar(row.codigoAlmacen)
      if (resp) {
        notSuccess('Registro eliminado correctamente')
        await refetch() // Refresh the table data after deletion
      } else {
        notDanger('Eliminación cancelada o no se pudo completar.')
      }
    } catch (error) {
      swalException(error)
      notDanger('Error al eliminar el upsert.')
    } finally {
      swalClose()
    }
  }
  return (
    <>
      <SimpleContainerBox maxWidth={'xl'}>
        <Breadcrumb routeSegments={[almacenRoutesMap.gestion]} />
        <StackMenu asideSidebarFixed>
          <StackMenuItem>
            <Button
              // size={'small'}
              variant="contained"
              onClick={() => setOpenNuevoAlmacen(true)}
              startIcon={<Newspaper />}
              color={'primary'}
            >
              Nuevo Almacen
            </Button>
          </StackMenuItem>
        </StackMenu>
        <Grid container>
          <Grid size={12}>
            <MaterialReactTable
              {...(MuiTableAdvancedOptionsProps as MRT_TableOptions<AlmacenProp>)}
              columns={columns}
              data={data ?? []}
              initialState={{
                showColumnFilters: true,
                columnVisibility: {
                  descripcion: false,
                },
              }}
              enableRowActions={true}
              manualFiltering
              manualPagination
              manualSorting
              muiToolbarAlertBannerProps={
                isError ? { color: 'error', children: 'Error loading data' } : undefined
              }
              onColumnFiltersChange={setColumnFilters}
              onPaginationChange={setPagination}
              onSortingChange={setSorting}
              enableDensityToggle={false}
              enableGlobalFilter={false}
              rowCount={rowCount}
              // localization={localization}
              state={{
                isLoading,
                columnFilters,
                pagination,
                showAlertBanner: isError,
                showProgressBars: isRefetching,
                density: 'compact',
                sorting,
                rowSelection,
              }}
              muiSearchTextFieldProps={{
                variant: 'outlined',
                placeholder: 'Busqueda',
                InputLabelProps: { shrink: true },
                size: 'small',
              }}
              positionActionsColumn={'first'}
              renderRowActions={({ row }) => (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: '0.5rem',
                    width: 100,
                  }}
                >
                  <SimpleMenu
                    menuButton={
                      <>
                        <IconButton aria-label="menuGestionProductos">
                          <MenuOpen />
                        </IconButton>
                      </>
                    }
                  >
                    <SimpleMenuItem
                      onClick={() => {
                        setOpenActualizarAlmacen(true)
                        setSelectedAlmacen(row.original)
                      }}
                    >
                      <Edit />
                      Editar
                    </SimpleMenuItem>

                    <SimpleMenuItem onClick={() => handleDeleteRow(row.original)}>
                      <Delete />
                      Eliminar
                    </SimpleMenuItem>
                  </SimpleMenu>
                  <AuditIconButton row={row.original} />
                </div>
              )}
              renderTopToolbarCustomActions={({ table }) => {
                return (
                  <Box
                    sx={{ display: 'flex', gap: '1rem', p: '0.5rem', flexWrap: 'wrap' }}
                  >
                    <IconButton onClick={handleRefreshTable} disabled={isRefetching}>
                      <Cached />
                    </IconButton>
                  </Box>
                )
              }}
              muiTableProps={{
                sx: {
                  tableLayout: 'fixed',
                },
              }}
              displayColumnDefOptions={{
                'mrt-row-actions': {
                  muiTableHeadCellProps: {
                    align: 'center',
                  },
                  size: 100,
                },
              }}
            />
          </Grid>
        </Grid>
        <AlmacenRegistroDialog
          id="unidadMedidaRegistroDialog"
          keepMounted={false}
          open={openOpenNuevoAlmacen}
          onClose={(value?: AlmacenBaseProp) => {
            refetch()
            setOpenNuevoAlmacen(false)
          }}
        />
        <AlmacenActualizarDialog
          keepMounted={false}
          open={openActualizarAlmacen}
          //@ts-ignore
          almacen={selectedAlmacen}
          onClose={(resp?: AlmacenBaseProp) => {
            setOpenActualizarAlmacen(false)
            setSelectedAlmacen(null)
            if (resp) {
              refetch().then()
            }
          }}
        />
      </SimpleContainerBox>
    </>
  )
}

export default AlmacenListado
